import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronRight, FileDown, Pencil, Trash2, Loader2, RotateCcw, MessageCircle, CalendarCheck, Star, Receipt } from "lucide-react";
import { ShareWhatsAppDialog } from "@/components/ShareWhatsAppDialog";
import { ConfirmVisitDialog } from "@/components/ConfirmVisitDialog";
import { RatingLinkDialog } from "@/components/RatingLinkDialog";
import { QuoteDialog } from "@/components/QuoteDialog";
import { QuoteCard } from "@/components/QuoteCard";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { ReportView } from "@/components/ReportView";
import { InspectionFormDialog } from "@/pages/admin/InspectionFormDialog";
import { api, downloadPdf, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const Btn = ({ children, ...p }) => (
  <button {...p} className={`h-11 px-4 rounded-lg border font-semibold text-sm flex items-center gap-2 transition-colors disabled:opacity-60 ${p.className || "border-slate-200 bg-white hover:bg-slate-100"}`}>{children}</button>
);

export default function InspectionDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { can } = useAuth();
  const [insp, setInsp] = useState(null);
  const [edit, setEdit] = useState(false);
  const [share, setShare] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [rate, setRate] = useState(false);
  const [quote, setQuote] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  const load = useCallback(() => api.get(`/inspections/${id}`).then((r) => setInsp(r.data)).catch((e) => toast.error(errMsg(e))), [id]);
  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const pdf = async () => {
    setPdfBusy(true);
    try {
      await downloadPdf(insp.id, insp.ref);
    } catch (e) {
      toast.error("تعذر إنشاء ملف PDF");
    } finally {
      setPdfBusy(false);
    }
  };

  const reopen = async () => {
    const { data } = await api.put(`/inspections/${id}`, { status: "in_progress" });
    setInsp(data);
    toast.success("أعيد فتح المعاينة للمندوب");
  };

  const remove = async () => {
    await api.delete(`/inspections/${id}`);
    toast.success("تم حذف المعاينة");
    nav("/admin");
  };

  if (!insp) return <div className="grid place-items-center h-64"><Loader2 className="animate-spin text-brand-red" /></div>;

  return (
    <div className="space-y-6" data-testid="inspection-detail-page">
      <Link to="/admin" className="text-sm text-slate-500 flex items-center gap-1 hover:text-slate-900" data-testid="back-to-dashboard"><ChevronRight size={16} />كل المعاينات</Link>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <span className="font-mono font-bold text-brand-navy" data-testid="detail-ref">{insp.ref}</span>
            <StatusBadge status={insp.status} testId="detail-status" />
          </div>
          <h1 className="font-heading text-3xl font-extrabold">كشف المعاينة والمقاسات</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <Btn onClick={pdf} disabled={pdfBusy} data-testid="export-pdf-button" className="border-brand-red bg-brand-red text-white hover:bg-brand-red-dark">
            {pdfBusy ? <Loader2 size={16} className="animate-spin" /> : <FileDown size={16} />} تصدير PDF
          </Btn>
          {insp.status !== "completed" && can("reports") && (
            <Btn onClick={() => setConfirm(true)} data-testid="confirm-visit-button">
              <CalendarCheck size={16} className="text-[#1EB257]" /> {insp.confirmation_sent_at ? "تم التأكيد ✓ إعادة الإرسال" : "تأكيد الموعد للعميل"}
            </Btn>
          )}
          {insp.status === "completed" && !insp.rating && can("reports") && (
            <Btn onClick={() => setRate(true)} data-testid="rating-link-button">
              <Star size={16} className="text-amber-500" /> {insp.rating_link_sent_at ? "إعادة إرسال رابط التقييم" : "طلب تقييم العميل"}
            </Btn>
          )}
          {insp.status === "completed" && !insp.quote && can("manage") && (
            <Btn onClick={() => setQuote(true)} data-testid="quote-button" className="border-brand-navy bg-brand-navy text-white hover:opacity-90">
              <Receipt size={16} /> إصدار عرض سعر مبدئي
            </Btn>
          )}
          {can("reports") && <Btn onClick={() => setShare(true)} data-testid="share-whatsapp-button" className="border-[#25D366] bg-[#25D366] text-white hover:bg-[#1EB257]">
            <MessageCircle size={16} /> إرسال واتساب
          </Btn>}
          {can("manage") && <>
          <Btn onClick={() => setEdit(true)} data-testid="edit-inspection-button"><Pencil size={16} /> تعديل / إعادة إسناد</Btn>
          {insp.status === "completed" && <Btn onClick={reopen} data-testid="reopen-inspection-button"><RotateCcw size={16} /> إعادة فتح</Btn>}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Btn data-testid="delete-inspection-button" className="border-red-200 bg-white text-red-700 hover:bg-red-50"><Trash2 size={16} /> حذف</Btn>
            </AlertDialogTrigger>
            <AlertDialogContent dir="rtl">
              <AlertDialogHeader><AlertDialogTitle className="text-start">حذف المعاينة {insp.ref} نهائياً؟</AlertDialogTitle></AlertDialogHeader>
              <AlertDialogFooter className="gap-2">
                <AlertDialogCancel data-testid="delete-cancel">إلغاء</AlertDialogCancel>
                <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700" data-testid="delete-confirm">حذف</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          </>}
        </div>
      </div>
      {insp.quote && <QuoteCard insp={insp} onEdit={can("manage") ? () => setQuote(true) : undefined} />}
      <ReportView insp={insp} />
      <QuoteDialog open={quote} onClose={() => setQuote(false)} insp={insp} onSaved={setInsp} />
      <InspectionFormDialog open={edit} onClose={() => setEdit(false)} initial={insp} onSaved={setInsp} />
      <ShareWhatsAppDialog open={share} onClose={() => setShare(false)} insp={insp} />
      <ConfirmVisitDialog open={confirm} onClose={() => setConfirm(false)} insp={insp} onSent={setInsp} />
      <RatingLinkDialog open={rate} onClose={() => setRate(false)} insp={insp} onSent={setInsp} />
    </div>
  );
}
